import { createApi, endpoint } from '@/shared/lib/http/endpoint';

import type { ClusterInfo } from './cluster-state';
import type { DeployVmListResponse } from './step1/vm-options';
import type { AzureCredentialBody } from './step2/credential-payload';
import type { ClusterStateResponse } from './step4/deployment-state';

// Backend wire types for /csp/list and /cluster/region-list aren't generated
// in cloud-typings yet (PbCsp namespace covers enums + Csp shape but not the
// list-RPC envelope). Local minimal app-shape types here; promote into
// cloud-typings if other modules need them.
export interface CspSummary {
  bizId?: string;
  csp?: string;
  // Operators switch a provider off here; /csp/list still returns it, so the
  // create picker filters on this rather than on the list's membership.
  available?: boolean;
  details?: { name?: string; logo?: string }[];
  // Present on the AWS entry; consumed by the account audit-log-export IAM
  // trust-policy guidance (the platform's trusted AWS account id).
  trusted_account_id?: string;
  accountId?: string;
  // Current policy templates + their versions. Cloud settings shows the
  // template in the create dialogs, and compares a credential's
  // `policy_version` against these to flag it as outdated.
  policy?: string;
  policy_version?: string;
  storage_policy?: string;
  storage_policy_version?: string;
  storage_policy_with_kms?: string;
  storage_policy_with_kms_assume_role?: string;
}

// One-shot IAM External ID for a cross-account role trust policy (audit-log
// export). Fetched fresh each time the create dialog opens.
export interface ExternalIdResponse {
  eid?: string;
}

export interface RegionSummary {
  cspId?: string;
  region?: string;
  // Region record id. The alert policy form posts the region NAME but keys its
  // select on bizId, so both are read.
  bizId?: string;
  // Localized region labels; the first entry is the display name.
  details?: { name?: string }[];
  // The versions this region offers, per release tier. Newer backends fill the
  // option list; older rows only carry the single fields. See step1/sr-version.
  sr_version_options?: { type?: string; version?: string; features?: string[] }[];
  srReleaseVersionsStable?: string[];
  srReleaseVersionsGA?: string[];
  srReleaseVersionsPreview?: string[];
  srReleaseVersion?: string;
  srReleaseVersionGA?: string;
  srReleaseVersionPreview?: string;
  sr_features_stable?: string[];
  sr_features_ga?: string[];
  sr_features_preview?: string[];
}

export interface CspListResponse {
  list?: CspSummary[];
}

export interface RegionListRequest {
  filters?: { available?: boolean; cspId?: string }[];
  // Server-side ordering. The alert policy form defaults to the FIRST region,
  // so it asks for a region-name sort; the sidebar doesn't care about order.
  common?: { orders?: { col: string; desc: boolean }[] };
}

export interface RegionListResponse {
  list?: RegionSummary[];
}

// /cluster/cluster-list returns the full cluster inventory. Support's ticket
// dialog reads only centralCluster {bizId, name} + the fields calClusterState
// derives from — ClusterInfo captures that slice.
export interface ClusterListResponse {
  list?: ClusterInfo[];
  total?: number;
}

// Best-effort: fetched with the error toast suppressed, so a failure just shows
// no version tag.
export interface SrReleaseThresholdsResponse {
  list?: { minor_version?: string; min_ga_version?: string; min_stable_version?: string }[];
}

export interface DeployClusterResponse {
  orderId?: string;
  errMsg?: string;
}

export interface CheckOrgFreeTierRequest {
  orgId: string;
}

export interface CheckOrgFreeTierResponse {
  freeTier?: boolean;
}

// `resource_struct_list` holds the cluster TYPES this account may still create
// — it gates the classic option, not view permission.
export interface LegacyClusterRecordResponse {
  resource_struct_list?: number[];
}

export interface ArchListResponse {
  // `arch` is the wire value (amd64 / arm64); show `display_arch` (x86 / Arm).
  list?: { arch?: string; display_arch?: string }[];
}

export const CpuArch = {
  AMD64: 'amd64',
  ARM64: 'arm64',
} as const;
export type CpuArch = (typeof CpuArch)[keyof typeof CpuArch];

export interface DeployVmListRequest {
  clusterType: number;
  vm: {
    common?: { limit?: number; orders?: { col: string; desc: boolean }[] };
    arch?: string;
    regionId: string;
    cspId: string;
    // 2 means "available only"; send it unless the caller wants every VM.
    available?: number;
  };
}

export interface FreeTierItem {
  resource_type?: number;
  vm_category_id?: string;
  num?: number;
}

export interface FreeTierListResponse {
  // PascalCase on the wire — the Go field has no json tag. See
  // schemas/step1-responses.ts.
  FreeTiers?: { items?: FreeTierItem[] }[] | null;
}

export interface HourPriceResponse {
  price?: {
    code?: number;
    errMsg?: string;
    packagePrice?: string;
    feUnitPrice?: string;
    beUnitPrice?: string;
    storageUnitPrice?: string;
  };
}

export interface UpsertResponse {
  // Absent on a refusal, which still answers 2xx.
  cid?: string;
  message?: string;
}

export interface CheckCredentialRequest {
  region_id: string;
  data_credential_id: string;
  deployment_credential_id: string;
}

export interface CheckCredentialResponse {
  // 20000 means the pair works. 0 / absent means the check did not run.
  check_err_code?: number;
  // The role the policy is missing, interpolated into the error copy.
  aws_missing_role?: string;
  azure_missing_role?: string;
  message?: string;
}

export interface QuickStartDeployResponse {
  quickStartUrl?: string;
  existingNetworkQuickStartUrl?: string;
}

export const clusterMetaApi = createApi({
  '/csp/list': endpoint<void, CspListResponse>('POST'),
  '/cluster/region-list': endpoint<RegionListRequest, RegionListResponse>('POST'),
  '/cluster/cluster-list': endpoint<void, ClusterListResponse>('POST'),
  '/cluster/get-external-id': endpoint<void, ExternalIdResponse>('POST'),
  // A 2xx means the check ran, not that it passed; read check_err_code.
  '/cluster/check-credential': endpoint<CheckCredentialRequest, CheckCredentialResponse>('POST'),
  // Not the `save-*` endpoints cloud settings uses, despite the same body, so
  // don't reuse `domains/cloud`'s mutations. A refusal is a 2xx with no `cid`.
  '/cluster/create-credential': endpoint<{ credential: unknown }, UpsertResponse>('POST'),
  '/cluster/create-storage-conf': endpoint<
    { storage_conf: unknown; credential_id?: string },
    UpsertResponse
  >('POST'),
  // Azure deployment credential: a flat body, no `{ credential }` wrapper.
  '/cluster/create-ak-sk-credential': endpoint<AzureCredentialBody, UpsertResponse>('POST'),
  '/cluster/check-org-free-tier': endpoint<CheckOrgFreeTierRequest, CheckOrgFreeTierResponse>(
    'POST',
  ),
  '/cluster/cluster-state': endpoint<{ bizId?: string; order_id?: string }, ClusterStateResponse>(
    'POST',
  ),
  // A refusal is a 2xx with no orderId.
  '/cluster/deploy-cluster-v2': endpoint<unknown, DeployClusterResponse>('POST'),
  '/cluster/arch-list': endpoint<{ regionId: string }, ArchListResponse>('POST'),
  '/cluster/deploy-VM-list': endpoint<DeployVmListRequest, DeployVmListResponse>('POST'),
  '/cluster/list-free-tier': endpoint<{ regionId: string; cspId: string }, FreeTierListResponse>(
    'POST',
  ),
  '/cluster/hour-price': endpoint<{ price: { packageUnits: unknown } }, HourPriceResponse>('POST'),
  '/cluster/check-cluster-name': endpoint<{ cluster_name: string }, { exist?: boolean }>('POST'),
  '/cluster/quick-start-deploy-v2': endpoint<unknown, QuickStartDeployResponse>('POST'),
  '/sr-release-ga-threshold/list': endpoint<void, SrReleaseThresholdsResponse>('POST'),
  '/account/legacy-cluster/account-record': endpoint<void, LegacyClusterRecordResponse>('POST'),
});
